import React from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { isEducatorPilotTier, tierDisplayLabel } from '../types/tiers';
import { BrandLogoIcon } from './BrandLogoIcon';

interface HeaderProps {
  onUpgradeClick?: () => void;
  /** Starts Pro checkout with 7-day trial ($9.99/mo price) */
  onTryFreeThenPay?: () => void;
  proCheckoutLoading?: boolean;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
  onManageSubscriptionClick?: () => void;
  onSettingsClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  onUpgradeClick,
  onTryFreeThenPay,
  proCheckoutLoading,
  onLoginClick,
  onSignupClick,
  onManageSubscriptionClick,
  onSettingsClick,
}) => {
  const { subscription, getRemainingRedactions } = useSubscription();
  const { user, signOut, loading } = useAuth();
  
  const isPremium = subscription.isActive && subscription.tier !== 'free';
  const remaining = getRemainingRedactions();
  const isTrialActive =
    subscription.isActive &&
    (subscription.status === 'trialing' ||
      (subscription.tier === 'monthly_pro' &&
        subscription.status !== 'active' &&
        !!subscription.expiresAt &&
        subscription.expiresAt > Date.now() &&
        subscription.expiresAt - Date.now() <= 7 * 24 * 60 * 60 * 1000));
  const trialDaysRemaining =
    isTrialActive && subscription.expiresAt
      ? Math.max(1, Math.ceil((subscription.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)))
      : 0;
  const statusEmail = user?.email
    ? user.email.length > 30
      ? `${user.email.slice(0, 27)}...`
      : user.email
    : '';
  const isRecurringSubscriber =
    isPremium && ['monthly_pro', 'annual_pro', 'campus_monthly', 'campus_annual'].includes(subscription.tier);
  const isPilot = isEducatorPilotTier(subscription.tier);
  const planStatusLabel = isTrialActive
    ? `Pro Trial — ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining`
    : subscription.cancelAtPeriodEnd
      ? 'Cancels at period end'
    : isPilot
      ? 'Complimentary Pilot Access'
    : subscription.isActive && ['monthly_pro', 'annual_pro', 'campus_monthly', 'campus_annual', 'district'].includes(subscription.tier)
      ? 'Pro Plan'
      : subscription.tier === 'day_pass'
        ? 'Day Pass'
        : 'Free Plan';

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header className="bg-white dark:bg-slate-800/50 shadow-sm sticky top-0 z-10 backdrop-blur-md">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BrandLogoIcon className="h-10 w-10 flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                <span className="text-indigo-800 dark:text-indigo-300">Redact</span> <span className="text-green-500 dark:text-green-400">AI</span>
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 -mt-1">
                The AI Preparation Layer for Education
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {!loading && (
              <>
                {user ? (
                  <>
                    {/* Usage indicator for free users */}
                    {!isPremium && (
                      <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <span>{remaining} free preparation{remaining !== 1 ? 's' : ''} left today</span>
                      </div>
                    )}
                    
                    {/* Premium badge or Upgrade button */}
                    {isPremium ? (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          {isPilot
                            ? 'Educator / Faculty Pilot'
                            : tierDisplayLabel(subscription.tier)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        {onTryFreeThenPay && (
                          <button
                            type="button"
                            onClick={onTryFreeThenPay}
                            disabled={proCheckoutLoading}
                            className="px-3 py-2 text-sm font-semibold rounded-lg border-[1.5px] border-[#cf9738] text-[#15452f] dark:text-[#cf9738] hover:bg-[#cf9738]/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                            style={{ fontFamily: "'Sora', sans-serif" }}
                          >
                            {proCheckoutLoading ? 'Starting…' : 'Try Pro Free for 7 Days'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={onUpgradeClick}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Upgrade to Pro
                        </button>
                      </div>
                    )}

                    {/* User Menu */}
                    <div className="flex items-center gap-2">
                      {onSettingsClick && (
                        <button
                          type="button"
                          onClick={onSettingsClick}
                          className="hidden sm:block px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          Settings
                        </button>
                      )}
                      {isPremium && onManageSubscriptionClick && (
                        <button
                          type="button"
                          onClick={onManageSubscriptionClick}
                          className="hidden sm:block px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          {isPilot
                            ? 'Account'
                            : isRecurringSubscriber || subscription.tier === 'day_pass'
                              ? 'Manage plan'
                              : 'Subscription'}
                        </button>
                      )}
                      <span className="hidden sm:block text-sm text-slate-600 dark:text-slate-300 truncate max-w-[150px]">
                        {user.email}
                      </span>
                      <button
                        onClick={handleSignOut}
                        className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
                      <a
                        href="#how-it-works"
                        className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        How It Works
                      </a>
                      <a
                        href="#pricing"
                        className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        Pricing
                      </a>
                    </nav>
                    <button
                      onClick={onLoginClick}
                      className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={onSignupClick}
                      className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      Try Pro Free for 7 Days
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {user && (
        <div
          className="px-4 py-2 text-sm text-white"
          style={{ backgroundColor: '#15452f' }}
        >
          <div className="container mx-auto flex items-center justify-between gap-3">
            <span className="truncate">
              Signed in as {statusEmail} |
            </span>
            <span
              className="inline-flex items-center rounded-full px-3 py-1 font-semibold"
              style={{ backgroundColor: '#cf9738', color: '#15452f' }}
            >
              {planStatusLabel}
            </span>
          </div>
        </div>
      )}
    </header>
  );
};
